import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiX, FiSearch } from "react-icons/fi";
import {
    getHostels,
    getRooms,
    getAllocations,
    allocateStudent,
    searchStudents,
} from "../../services/hostelService";

const currentAcademicYear = () => {
    const now = new Date();
    const y = now.getMonth() >= 5 ? now.getFullYear() : now.getFullYear() - 1; // June cutoff, adjust if your AY starts differently
    return `${y}-${y + 1}`;
};

const getCourseLabel = (s) => {
    if (!s) return "";
    const cName = typeof s.course === "object" && s.course !== null
        ? (s.course.name || s.course.courseName || s.course.shortName || "")
        : (typeof s.courseAdmitted === "string" ? s.courseAdmitted : (s.course || ""));
    const yr = s.yearNumber ? `Yr ${s.yearNumber}` : (s.courseYear ? `Yr ${s.courseYear}` : "");
    const div = s.divisionName ? `(Div ${s.divisionName})` : "";
    return [cName, yr, div].filter(Boolean).join(" ");
};

export default function AllocateModal({ defaultHostelId, onClose, onSuccess }) {
    const [studentQuery, setStudentQuery] = useState("");
    const [studentResults, setStudentResults] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [searching, setSearching] = useState(false);

    const [hostels, setHostels] = useState([]);
    const [selectedHostel, setSelectedHostel] = useState(defaultHostelId || "");
    const [rooms, setRooms] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState("");
    const [takenBeds, setTakenBeds] = useState([]);
    const [selectedBed, setSelectedBed] = useState("");

    const [academicYear, setAcademicYear] = useState(currentAcademicYear());
    const [remarks, setRemarks] = useState("");
    const [saving, setSaving] = useState(false);

    // Load hostel list once
    useEffect(() => {
        getHostels()
            .then((res) => setHostels(res.data || []))
            .catch(() => toast.error("Failed to load hostels"));
    }, []);

    // Debounced student search
    useEffect(() => {
        if (!studentQuery.trim()) {
            setStudentResults([]);
            return;
        }
        setSearching(true);
        const timer = setTimeout(() => {
            searchStudents(studentQuery)
                .then((res) => setStudentResults(res.data || []))
                .catch(() => toast.error("Student search failed"))
                .finally(() => setSearching(false));
        }, 350);
        return () => clearTimeout(timer);
    }, [studentQuery]);

    // Load rooms when hostel changes
    useEffect(() => {
        if (!selectedHostel) {
            setRooms([]);
            return;
        }
        getRooms(selectedHostel)
            .then((res) => setRooms((res.data || []).filter((r) => r.status !== "Maintenance" && r.occupiedBeds < r.capacity)))
            .catch(() => toast.error("Failed to load rooms"));
        setSelectedRoom("");
        setSelectedBed("");
    }, [selectedHostel]);

    // Load taken bed numbers when room changes
    useEffect(() => {
        if (!selectedRoom) {
            setTakenBeds([]);
            return;
        }
        getAllocations({ room: selectedRoom, status: "Active" })
            .then((res) => setTakenBeds((res.data || []).map((a) => a.bedNumber)))
            .catch(() => toast.error("Failed to check bed availability"));
        setSelectedBed("");
    }, [selectedRoom]);

    const roomObj = rooms.find((r) => r._id === selectedRoom);
    const availableBeds = roomObj
        ? Array.from({ length: roomObj.capacity }, (_, i) => i + 1).filter(
            (n) => !takenBeds.includes(n),
        )
        : [];

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedStudent) return toast.error("Select a student");
        if (!selectedHostel) return toast.error("Select a hostel");
        if (!selectedRoom) return toast.error("Select a room");
        if (!selectedBed) return toast.error("Select a bed");
        if (!academicYear.trim()) return toast.error("Academic year is required");

        setSaving(true);
        try {
            await allocateStudent({
                student: selectedStudent._id,
                hostel: selectedHostel,
                room: selectedRoom,
                bedNumber: Number(selectedBed),
                academicYear,
                remarks,
            });
            toast.success("Student allocated successfully");
            onSuccess();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to allocate student");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                    <h2>Allocate Student to Hostel</h2>
                    <button type="button" className="btn btn-icon" onClick={onClose}>
                        <FiX />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Student search */}
                    <div className="form-group" style={{ marginBottom: "1rem" }}>
                        <label className="form-label">
                            Student<span style={{ color: "var(--danger)" }}> *</span>
                        </label>
                        <div className="student-select-box">
                            {!selectedStudent ? (
                                <>
                                    <div className="student-select-search">
                                        <FiSearch />
                                        <input
                                            placeholder="Search student by name or roll number..."
                                            value={studentQuery}
                                            onChange={(e) => setStudentQuery(e.target.value)}
                                        />
                                    </div>
                                    {studentQuery && (
                                        <div className="student-select-list">
                                            {searching && <div className="student-select-item">Searching...</div>}
                                            {!searching && studentResults.length === 0 && (
                                                <div className="student-select-item">No students found</div>
                                            )}
                                            {!searching &&
                                                studentResults.map((s) => (
                                                    <button
                                                        type="button"
                                                        key={s._id}
                                                        className="student-select-item"
                                                        onClick={() => {
                                                            setSelectedStudent(s);
                                                            setStudentResults([]);
                                                        }}
                                                    >
                                                        <div className="student-name">{s.fullName}</div>
                                                        <div className="student-meta">
                                                            {s.rollNo ? `Roll ${s.rollNo} · ` : (s.enrollmentNo ? `Reg ${s.enrollmentNo} · ` : "")}
                                                            {getCourseLabel(s)}
                                                        </div>
                                                    </button>
                                                ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="student-select-list">
                                    <button
                                        type="button"
                                        className="student-select-item selected"
                                        title="Click to change"
                                        onClick={() => {
                                            setSelectedStudent(null);
                                            setStudentQuery("");
                                        }}
                                    >
                                        <div className="student-name">{selectedStudent.fullName}</div>
                                        <div className="student-meta">
                                            {selectedStudent.rollNo ? `Roll ${selectedStudent.rollNo} · ` : (selectedStudent.enrollmentNo ? `Reg ${selectedStudent.enrollmentNo} · ` : "")}
                                            {getCourseLabel(selectedStudent)} · Click to change
                                        </div>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Hostel / Room / Bed */}
                    <div className="form-grid">
                        <div className="form-group">
                            <label className="form-label">
                                Hostel<span style={{ color: "var(--danger)" }}> *</span>
                            </label>
                            <select
                                className="form-control"
                                value={selectedHostel}
                                onChange={(e) => setSelectedHostel(e.target.value)}
                            >
                                <option value="">Select hostel</option>
                                {hostels.map((h) => (
                                    <option key={h._id} value={h._id}>
                                        {h.name} ({h.type})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                Room<span style={{ color: "var(--danger)" }}> *</span>
                            </label>
                            <select
                                className="form-control"
                                value={selectedRoom}
                                onChange={(e) => setSelectedRoom(e.target.value)}
                                disabled={!selectedHostel}
                            >
                                <option value="">
                                    {selectedHostel ? "Select room" : "Select hostel first"}
                                </option>
                                {rooms.map((r) => (
                                    <option key={r._id} value={r._id}>
                                        Room {r.roomNumber} ({r.occupiedBeds}/{r.capacity} filled)
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                Bed<span style={{ color: "var(--danger)" }}> *</span>
                            </label>
                            <select
                                className="form-control"
                                value={selectedBed}
                                onChange={(e) => setSelectedBed(e.target.value)}
                                disabled={!selectedRoom}
                            >
                                <option value="">
                                    {selectedRoom ? "Select bed" : "Select room first"}
                                </option>
                                {availableBeds.map((n) => (
                                    <option key={n} value={n}>
                                        Bed {n}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                Academic Year<span style={{ color: "var(--danger)" }}> *</span>
                            </label>
                            <input
                                className="form-control"
                                value={academicYear}
                                onChange={(e) => setAcademicYear(e.target.value)}
                                placeholder="e.g. 2026-2027"
                            />
                        </div>

                        <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                            <label className="form-label">Remarks</label>
                            <input
                                className="form-control"
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                placeholder="Optional"
                            />
                        </div>
                    </div>

                    <div className="modal-actions" style={{ marginTop: "1.25rem" }}>
                        <button type="button" className="btn btn-outline" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            {saving ? "Allocating..." : "Allocate"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}